/**
 * Robust API Client - Anti-Fragility Design
 *
 * SOLVES THESE PERSISTENT ISSUES:
 * 1. APIs silently failing with empty results instead of clear errors
 * 2. Missing auth setup causing mysterious 401 responses
 * 3. Optional token setters that developers forget to configure
 * 4. Silent failures that aren't detected until production
 *
 * DESIGN PRINCIPLES:
 * 1. DEPENDENCY INJECTION: Auth tokens required at construction, not optional
 * 2. FAIL-FAST: Throw immediately if auth is missing/invalid
 * 3. VALIDATION: Built-in checks prevent silent failures
 * 4. RECOVERY: Automatic retry with token refresh
 * 5. OBSERVABILITY: Detailed logging and error reporting
 */

import { getRuntimeConfig } from '../config/runtimeConfig';

// Response interfaces
interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  metadata?: {
    timestamp: string;
    requestId: string;
  };
}

interface ApiError {
  error: {
    code: string;
    message: string;
    details?: any;
    timestamp: string;
    requestId: string;
    retryable?: boolean;
  };
}

// Auth token provider interface
interface AuthTokenProvider {
  getAccessToken(): Promise<string>;
  refreshTokens(): Promise<void>;
  validateAuth(): void; // Throws if auth is invalid
}

interface RequestConfig extends RequestInit {
  retries?: number;
  timeout?: number;
  validateAuth?: boolean;
}

class RobustApiClient {
  private baseUrl: string;
  private authProvider: AuthTokenProvider;
  private readonly maxRetries = 3;
  private readonly defaultTimeout = 30000; // 30 seconds
  private readonly retryDelay = 1000; // 1 second

  constructor(authProvider: AuthTokenProvider, baseUrl?: string) {
    // FAIL-FAST: Require auth provider at construction
    if (!authProvider) {
      throw new Error(
        'CONSTRUCTION_ERROR: AuthTokenProvider is required. ' +
        'This prevents the common issue of forgetting to set up authentication. ' +
        'Pass a valid auth provider to the constructor.'
      );
    }

    // Get base URL from runtime config if not provided
    const finalBaseUrl = baseUrl || (() => {
      try {
        return getRuntimeConfig().apiUrl;
      } catch {
        return '';
      }
    })();

    if (!finalBaseUrl) {
      throw new Error('CONSTRUCTION_ERROR: Base URL is required for API client - configure runtime config');
    }

    this.authProvider = authProvider;
    this.baseUrl = finalBaseUrl;

    // Only log in development
    if (process.env.NODE_ENV === 'development') {
      console.log('✅ ROBUST API CLIENT: Initialized with auth provider', { baseUrl });
    }
  }

  /**
   * FAIL-FAST: Validate that auth is properly configured before making requests
   */
  private async validateAuthentication(): Promise<{ accessToken: string; }> {
    try {
      // This will throw if auth is not properly set up
      this.authProvider.validateAuth();

      const accessToken = await this.authProvider.getAccessToken();

      if (!accessToken) {
        throw new Error('ACCESS_TOKEN_EMPTY: Auth provider returned empty access token');
      }

      return { accessToken };

    } catch (error) {
      throw new Error(`AUTH_VALIDATION_FAILED: ${error}`);
    }
  }

  private normalizeEndpoint(endpoint: string): string {
    if (this.baseUrl.endsWith('/api')) {
      // Base URL already includes /api (localhost development)
      // Just append the endpoint directly
      return endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    } else {
      // Base URL doesn't include /api (Azure production)
      // Add /api prefix to the endpoint
      return endpoint.startsWith('/api/') ? endpoint :
        endpoint.startsWith('/') ? `/api${endpoint}` :
          `/api/${endpoint}`;
    }
  }

  private async setupRequestHeaders(
    requestConfig: RequestConfig,
    validateAuth: boolean
  ): Promise<Record<string, string>> {
    // VALIDATION: Ensure auth is valid before each request
    let accessToken = '';

    if (validateAuth) {
      const tokens = await this.validateAuthentication();
      accessToken = tokens.accessToken;
    }

    // Create headers with auth
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...requestConfig.headers as Record<string, string>
    };

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }

    // Development bypass (only for localhost)
    if (process.env.NODE_ENV === 'development' && window.location.hostname === 'localhost') {
      headers['x-skip-auth'] = 'development';
      console.warn('⚠️ DEVELOPMENT MODE: Auth bypass enabled');
    }

    return headers;
  }

  private async handleHttpError(
    response: Response,
    attempt: number,
    retries: number
  ): Promise<{ shouldRetry: boolean; error?: Error }> {

    if (response.status === 401) {
      return this.handleAuthError(attempt, retries);
    }

    if (response.status === 403) {
      return this.handlePermissionError(response, attempt, retries);
    }

    if (response.status >= 500 && attempt < retries) {
      return this.handleServerError(response, attempt, retries);
    }

    // Default case: don't retry for other HTTP errors
    return { shouldRetry: false };
  }

  private async handleAuthError(attempt: number, retries: number) {
    console.warn(`🔒 AUTH ERROR [${attempt}/${retries}]: 401 Unauthorized`);

    if (attempt < retries) {
      // Try to refresh tokens and retry
      console.log('🔄 Attempting token refresh...');
      await this.authProvider.refreshTokens();
      return { shouldRetry: true };
    }

    return {
      shouldRetry: false,
      error: new Error(`AUTHENTICATION_FAILED: Received 401 after ${retries} attempts. Check auth configuration.`)
    };
  }

  private async handlePermissionError(response: Response, attempt: number, retries: number) {
    console.warn(`🚫 PERMISSION ERROR [${attempt}/${retries}]: 403 Forbidden`);

    // Try to parse error response for better messaging
    let errorMessage = 'Access denied - insufficient permissions';
    try {
      const errorData = await response.json();
      errorMessage = errorData.error?.message || errorData.message || errorMessage;
    } catch {
      // Keep default error message if response isn't JSON
    }

    // Don't retry 403 errors - they won't resolve with retries
    const error = new Error(`PERMISSION_DENIED: ${errorMessage}`) as Error & {
      status: number;
      isPermissionError: boolean;
      userMessage: string;
    };
    error.status = 403;
    error.isPermissionError = true;
    error.userMessage = 'You do not have permission to access this resource. Contact your administrator if you need access.';

    return { shouldRetry: false, error };
  }

  private handleServerError(response: Response, attempt: number, retries: number) {
    console.warn(`🔄 SERVER ERROR [${attempt}/${retries}]: ${response.status}, retrying...`);

    return {
      shouldRetry: true
    };
  }

  /**
   * ROBUST: Make HTTP request with automatic retries, token refresh, and error handling
   */
  private async makeRequest<T>(
    endpoint: string,
    config: RequestConfig = {}
  ): Promise<ApiResponse<T>> {
    const {
      retries = this.maxRetries,
      timeout = this.defaultTimeout,
      validateAuth = true,
      ...requestConfig
    } = config;

    // CRITICAL FIX: Handle different API URL formats between environments
    //
    // Problem: Different environments configure REACT_APP_API_URL differently:
    // - Localhost (.env.local): "http://localhost:3000/api" (includes /api suffix)
    // - Azure (GitHub Actions): "https://lseg-teams-express...io" (no /api suffix)
    //
    // Without this logic, we get incorrect URLs:
    // - Localhost: http://localhost:3000/api/api/policies ❌ DOUBLE /api
    // - Azure: https://lseg-teams-express...io/policies ❌ MISSING /api
    //
    // This fix ensures correct URLs in both environments:
    // - Localhost: http://localhost:3000/api/policies ✅ CORRECT
    // - Azure: https://lseg-teams-express...io/api/policies ✅ CORRECT
    const url = `${this.baseUrl}${this.normalizeEndpoint(endpoint)}`;
    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= retries; attempt++) {
      try {
        const response = await this.executeSingleRequest(url, requestConfig, validateAuth, timeout, attempt, retries);
        return await this.parseSuccessResponse<T>(response, requestConfig, url, attempt, retries);
      } catch (error) {
        lastError = await this.handleRequestError(error as Error, url, attempt, retries);
      }
    }

    throw new Error(`REQUEST_EXHAUSTED: Failed after ${retries} attempts. Last error: ${lastError?.message}`);
  }

  private async executeSingleRequest(
    url: string,
    requestConfig: RequestInit,
    validateAuth: boolean,
    timeout: number,
    attempt: number,
    retries: number
  ): Promise<Response> {
    console.log(`🌐 API REQUEST [${attempt}/${retries}]: ${requestConfig.method || 'GET'} ${url}`);

    const headers = await this.setupRequestHeaders(requestConfig, validateAuth);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch(url, {
        ...requestConfig,
        headers,
        signal: controller.signal
      });

      clearTimeout(timeoutId);
      await this.handleResponseErrors(response, attempt, retries);
      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      throw error;
    }
  }

  private async handleResponseErrors(response: Response, attempt: number, retries: number): Promise<void> {
    if (!response.ok) {
      const errorResult = await this.handleHttpError(response, attempt, retries);

      if (errorResult.shouldRetry) {
        await this.delay(this.retryDelay * attempt);
        throw new Error('RETRY_REQUESTED');
      }
      if (errorResult.error) {
        throw errorResult.error;
      }

      const errorMessage = await this.parseErrorMessage(response);
      throw new Error(`HTTP_ERROR: ${errorMessage}`);
    }
  }

  private async parseErrorMessage(response: Response): Promise<string> {
    let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
    try {
      const errorData = await response.json();
      errorMessage = errorData.error?.message || errorData.message || errorMessage;
    } catch {
      // Keep default error message if response isn't JSON
    }
    return errorMessage;
  }

  private async parseSuccessResponse<T>(
    response: Response,
    requestConfig: RequestInit,
    url: string,
    attempt: number,
    retries: number
  ): Promise<ApiResponse<T>> {
    const data = await response.json();
    console.log(`✅ API SUCCESS [${attempt}/${retries}]: ${requestConfig.method || 'GET'} ${url}`);
    return data;
  }

  private async handleRequestError(error: Error, url: string, attempt: number, retries: number): Promise<Error> {
    console.error(`❌ API ERROR [${attempt}/${retries}]:`, error);

    if (error.message === 'RETRY_REQUESTED') {
      return error;
    }

    if (this.isConnectionError(error)) {
      throw this.createBackendUnavailableError(url);
    }

    if (this.isNonRetryableError(error)) {
      throw error;
    }

    if (attempt < retries) {
      console.log(`🔄 Retrying in ${this.retryDelay * attempt}ms...`);
      await this.delay(this.retryDelay * attempt);
    }

    return error;
  }

  private isConnectionError(error: Error): boolean {
    return error instanceof TypeError && error.message === 'Failed to fetch';
  }

  private isNonRetryableError(error: Error): boolean {
    return error.message.includes('AUTH_VALIDATION_FAILED') || 
           error.message.includes('CONSTRUCTION_ERROR');
  }

  private createBackendUnavailableError(url: string): Error {
    const backendUrl = new URL(url);
    const errorMsg = `BACKEND_UNAVAILABLE: Cannot connect to backend server at ${backendUrl.origin}. Please ensure the backend server is running.`;
    console.error('🔴 ' + errorMsg);

    const backendError = new Error(errorMsg) as Error & {
      isBackendUnavailable: boolean;
      userMessage: string;
      technicalDetails: string;
    };
    backendError.isBackendUnavailable = true;
    backendError.userMessage = `Backend server is not running. Please start the backend server at ${backendUrl.origin} and refresh the page.`;
    backendError.technicalDetails = `Connection refused to ${url}`;
    return backendError;
  }

  /**
   * Utility: Delay for retries
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // HTTP Methods
  async get<T>(endpoint: string, config?: RequestConfig): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(endpoint, { ...config, method: 'GET' });
  }

  async post<T>(endpoint: string, data?: any, config?: RequestConfig): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(endpoint, {
      ...config,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined
    });
  }

  async put<T>(endpoint: string, data?: any, config?: RequestConfig): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(endpoint, {
      ...config,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined
    });
  }

  async delete<T>(endpoint: string, config?: RequestConfig): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(endpoint, { ...config, method: 'DELETE' });
  }

  async patch<T>(endpoint: string, data?: any, config?: RequestConfig): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(endpoint, {
      ...config,
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined
    });
  }

  /**
   * Health check method - validates both API and auth
   */
  async healthCheck(): Promise<{ api: boolean; auth: boolean; details: any }> {
    try {
      // Test auth validation
      const authCheck = await this.validateAuthentication();

      // Test API connectivity
      const apiCheck = await this.get('/health', { validateAuth: false });

      return {
        api: true,
        auth: true,
        details: {
          authTokens: {
            hasAccessToken: !!authCheck.accessToken,
          },
          apiResponse: apiCheck
        }
      };
    } catch (error) {
      return {
        api: false,
        auth: false,
        details: { error: error instanceof Error ? error.message : 'Unknown error' }
      };
    }
  }
}

export { RobustApiClient, type AuthTokenProvider, type ApiResponse, type ApiError };
